import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoMetadata from '../seo/metadata';
import seoStructuredData from '../seo/structuredData';
import generatedCanonicals from '../seo/generatedCanonicals';
import { applySeo } from '../seo/applySeo';

const { DEFAULT_OG_IMAGE, buildMeta, resolvePage, absoluteUrl, isPrivatePath } = seoMetadata;
const { buildPageGraph } = seoStructuredData;
const { CANONICAL_OVERRIDES } = generatedCanonicals;

/** Duplicate game slugs point at the URL we want to rank; everything else is its own canonical. */
const canonicalPathFor = (pathname) => {
  const clean = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return CANONICAL_OVERRIDES[clean] || clean;
};

const NOINDEX = 'noindex, nofollow';

/**
 * Keeps head tags in step with the current route for the whole app.
 *
 * Deliberately driven by the same buildMeta/buildPageGraph used by the build step, so a page reached
 * by clicking a link ends up with byte-identical tags to the same page loaded directly. Where those
 * two disagree, Google can index a title the user never sees.
 *
 * Pages with their own data-driven metadata (an ad, a seller profile) let this run first for a
 * sensible default and then overwrite it once loaded.
 */
export function usePageSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const image = absoluteUrl(DEFAULT_OG_IMAGE);

    if (isPrivatePath(pathname)) {
      const meta = buildMeta('home');
      applySeo({
        title: meta.title,
        description: meta.description,
        canonical: null,
        robots: NOINDEX,
        image,
        jsonLd: null,
      });
      return;
    }

    const page = resolvePage(pathname);

    if (!page) {
      // A route resolved at runtime (/ad/:id, /users/:username) or an unknown path. Self-canonical
      // keeps the URL eligible to rank; the page itself fills in a real title once it has data.
      applySeo({ canonical: absoluteUrl(pathname), image, jsonLd: null });
      return;
    }

    const meta = buildMeta(page.pageType, page.slug);
    const canonicalPath = canonicalPathFor(pathname);
    const canonical = absoluteUrl(canonicalPath);

    // Structured data describes the canonical page, so duplicate slugs reuse the preferred URL.
    const canonicalPage = resolvePage(canonicalPath) || page;

    applySeo({
      title: meta.title,
      description: meta.description,
      canonical,
      image,
      jsonLd: buildPageGraph(canonicalPage, meta, canonical),
    });
  }, [pathname]);
}

export default usePageSeo;
